import { formatSessionDate } from './format'

/**
 * What History is allowed to say about how much of the history it has.
 *
 * ── WHY THIS IS A MODULE AND NOT THREE `&&`s IN A COMPONENT ─────────────────
 *
 * §4.5 is four clauses: state the loaded range, expose further pages until
 * `next_cursor` is null, **do not label the first page as all history**, and
 * distinguish "no match yet" from an exhausted history. Criterion 16 turns the
 * third into an acceptance item — "loading and partial-range states never imply
 * that a partial list is complete".
 *
 * The first version of Today was a run of `&&` expressions, one per state its
 * author expected, and `scanning` was not among them: that combination rendered
 * **nothing at all** (`Today.tsx:162-173`). A blank screen is the worst of the
 * available failures, because it looks like a screen with nothing to say rather
 * than like a bug. Same hazard here, and worse — a History screen that renders
 * no range line has not said "so far", which is precisely the claim §4.5 is
 * trying to prevent it from making by omission.
 *
 * So: a total function over the list's flags, a flat union with one member per
 * sentence, and a `switch` with no `default` at the call site. Adding a state
 * without a sentence then fails `tsc` rather than blanking a line.
 */

/** Which button a failed read offers. They are not interchangeable — see below. */
export type RetryAction = 'reload' | 'loadMore'

export type HistoryRange =
  /** The first page has not landed. */
  | { kind: 'reading' }
  /** Read to the end; there is nothing, and there never was. */
  | { kind: 'none_yet' }
  /** Nothing arrived at all, and a request failed. Not the same as having none. */
  | { kind: 'none_read'; retry: RetryAction }
  /**
   * A page LANDED and was empty, and then a later read failed.
   *
   * Split from `none_read` because that sentence is false here: the first page
   * really was read, and it really had nothing in it. The two were one kind
   * while `pagesLoaded` was doing two jobs at once -- choosing the retry action
   * and silently deciding a sentence.
   *
   * **Not reachable against today's backend, and kept anyway.** The API
   * over-fetches `limit + 1`, so an empty page always carries a null cursor and
   * cannot then fail. It is reachable in this client's own state space --
   * `History.tsx` already guards for it -- and §4.5 anticipates
   * condition-filtered history, which is where it arrives for real. Unit tested,
   * deliberately no e2e: an e2e for a state no fixture can produce would be a
   * test asserting its own mock.
   */
  | { kind: 'none_stalled'; retry: RetryAction }
  /** Nothing so far, and pages remain. §4.5's "no match yet" against "exhausted". */
  | { kind: 'none_so_far' }
  /** Every page read. The ONLY member allowed to say "whole". */
  | { kind: 'whole'; earliest: string; latest: string }
  /** A further page is in flight. */
  | { kind: 'paging'; earliest: string; latest: string }
  /** A page failed. What arrived stays on screen; the rest is unknown. */
  | { kind: 'stalled'; earliest: string; latest: string; retry: RetryAction }
  /** Pages remain and nobody has asked for them. */
  | { kind: 'more'; earliest: string; latest: string }

/**
 * Structural rather than `PagedList<RecordingRecord>`, so a test can state a
 * combination of flags directly instead of driving a hook into it. `PagedList`
 * is assignable to it.
 */
export interface HistoryRangeInput {
  items: readonly { session_date: string }[]
  isLoading: boolean
  isPaging: boolean
  isComplete: boolean
  error: unknown
  pagesLoaded: number
}

export function historyRange(list: HistoryRangeInput): HistoryRange {
  if (list.isLoading) return { kind: 'reading' }

  /**
   * THE TWO "TRY AGAIN"S ARE DIFFERENT ACTIONS, and picking the wrong one is
   * destructive rather than merely useless.
   *
   * A first page that failed has nothing to keep, so `reload()` is right. A
   * LATER page that failed has pages on screen that did arrive, and `reload()`
   * would throw them away to re-fetch them — the inverse of the rule
   * `Today.tsx:119-124` states, that a read which failed part way does not
   * discard what it read. `loadMore()` retries only the page that failed,
   * because a failed request never advanced `nextCursor`.
   */
  const retry: RetryAction = list.pagesLoaded === 0 ? 'reload' : 'loadMore'

  /**
   * MIN AND MAX, not the first and last row.
   *
   * The list arrives ordered by `created_at` and this column shows
   * `session_date` (`api.ts:369-376`). On the default fixtures page one is
   * [Sep 11, Aug 30], so "first row to last row" reads backwards and names a
   * range the user does not have. Plain string comparison is correct and
   * zone-free for `YYYY-MM-DD`; `calendarDateParts` is for arithmetic, and there
   * is none here.
   */
  let earliest: string | null = null
  let latest: string | null = null
  for (const { session_date } of list.items) {
    if (earliest === null || session_date < earliest) earliest = session_date
    if (latest === null || session_date > latest) latest = session_date
  }

  // An error is checked before completeness in both halves, deliberately. The
  // two cannot both be true today -- a failed page leaves `nextCursor` set, so
  // `isComplete` stays false -- but if that ever changes, the safe direction is
  // to report the failure rather than to claim the list is whole.
  if (earliest === null || latest === null) {
    // `pagesLoaded` decides WHICH of the two empty failures this is, and it is
    // the same fact that decides the retry action -- a page landed, or none
    // did. Written once rather than inferred twice.
    if (list.error != null) {
      return list.pagesLoaded === 0
        ? { kind: 'none_read', retry: 'reload' }
        : { kind: 'none_stalled', retry: 'loadMore' }
    }
    if (list.isComplete) return { kind: 'none_yet' }
    return { kind: 'none_so_far' }
  }

  if (list.error != null) return { kind: 'stalled', earliest, latest, retry }
  if (list.isComplete) return { kind: 'whole', earliest, latest }
  if (list.isPaging) return { kind: 'paging', earliest, latest }
  return { kind: 'more', earliest, latest }
}

export interface HistoryRangeText {
  /** What the screen has, and has not, read. Never carries the failure. */
  text: string
  /**
   * The failure sentence, or null. Rendered by the notice, which is the §7.1
   * surface -- it is the only one that can carry the server's `detail` and its
   * `code`, which `text` has no way to hold.
   *
   * Splitting them is the fix for a screen that contradicted itself: the notice
   * said "so this is not all of it", implying a partial list, next to a range
   * line saying "None of your history could be read." Both sentences were about
   * completeness and only one of them could be right. Now one predicate decides
   * both, so they cannot disagree.
   */
  failure: string | null
  /**
   * Which action the screen's ONE retry performs. It used to mean "the button
   * this line renders", and the line no longer renders one -- there was a
   * second control forty pixels from the notice's, with the same accessible
   * name and, on `stalled`, the opposite behaviour.
   *
   * Null when there is nothing to retry.
   */
  retry: RetryAction | null
}

/**
 * The sentence for each state.
 *
 * Copy in `lib/` rather than in the component, as `RECORDING_OUTCOME` and
 * `WARNING_COPY` already are — because the property that matters about these
 * eight strings is a property OF THE SET, and a set spread across JSX cannot be
 * tested. Exactly one of them may use the word "whole", and a test asserts the
 * other seven do not; that is what catches the copy edit which is how this rule
 * actually gets broken, months from now, by someone tightening a sentence.
 */
export function historyRangeText(range: HistoryRange): HistoryRangeText {
  const span = (earliest: string, latest: string) =>
    earliest === latest
      ? `one recording, ${formatSessionDate(earliest)}`
      : `recordings from ${formatSessionDate(earliest)} to ${formatSessionDate(latest)}`

  switch (range.kind) {
    case 'reading':
      return { text: 'Reading your history…', failure: null, retry: null }
    case 'none_yet':
      return { text: 'You have no recordings yet.', failure: null, retry: null }
    /**
     * The extent sentence says what is NOT known, in the shape `UserDetail`
     * already uses for the same situation ("not evidence that there are none").
     * The old wording -- "None of your history could be read." -- was the
     * failure and the extent in one string, which is how it ended up beside a
     * notice saying the opposite.
     */
    case 'none_read':
      return {
        text: 'No recordings are shown, so this is not evidence that you have none.',
        failure: 'Your history could not be loaded.',
        retry: range.retry,
      }
    case 'none_stalled':
      return {
        text:
          'No recordings on the pages read so far, so this is not evidence that you have none.',
        failure: 'The next page of your history could not be loaded.',
        retry: range.retry,
      }
    case 'none_so_far':
      return {
        text: 'No recordings on the pages read so far. There is more history to load.',
        failure: null,
        retry: null,
      }
    case 'whole': {
      const { earliest, latest } = range
      return {
        text:
          earliest === latest
            ? `Your whole history: one recording, ${formatSessionDate(earliest)}.`
            : `Your whole history, from ${formatSessionDate(earliest)} to ${formatSessionDate(latest)}.`,
        failure: null,
        retry: null,
      }
    }
    case 'paging':
      return {
        text: `Showing ${span(range.earliest, range.latest)} so far. Loading more…`,
        failure: null,
        retry: null,
      }
    case 'stalled':
      return {
        // "loaded" for a transport failure, "read" for a payload that arrived
        // unusable -- the distinction `dailyStatus.ts:321` and `:330` already
        // make. This line used to say "read" for a request that never arrived,
        // beside a notice that said "loaded" about the same event.
        text: `Showing ${span(range.earliest, range.latest)} so far. This is not everything you have recorded.`,
        failure: 'The next page of your history could not be loaded.',
        retry: range.retry,
      }
    case 'more':
      return {
        text: `Showing ${span(range.earliest, range.latest)}. Load more below for older recordings.`,
        failure: null,
        retry: null,
      }
  }
}
